import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getRequest, transitionRequest } from "../services/api";
import StatusBadge from "../components/StatusBadge";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Gem, CheckCircle2, XCircle, Undo2, Flag, Send, Clock } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "\u2014";

export default function RequestDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => getRequest(id).then((d) => { setReq(d.request); setHistory(d.history || []); }).catch(() => setReq(false)).finally(() => setLoading(false));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <div className="text-vault-panel">Loading\u2026</div>;
  if (!req) return <div className="text-vault-panel">Request not found or access denied.</div>;

  const act = async (status, successMsg) => {
    setBusy(true);
    try {
      await transitionRequest(req.id, status, notes);
      setNotes("");
      await load();
      await refresh();
      toast.success(successMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    } finally { setBusy(false); }
  };

  const Field = ({ label, children }) => (
    <div><div className="text-xs text-vault-subtle mb-1">{label}</div><div className="text-sm text-vault-cream font-medium">{children}</div></div>
  );

  const VARIANT_CLASSES = {
    accent: "bg-vault-accent text-vault-panel hover:bg-vault-accentdark",
    danger: "bg-red-900/40 text-red-300 hover:bg-red-900/60",
    default: "bg-vault-panel3 text-vault-cream hover:bg-vault-panel2",
  };

  const Btn = ({ onClick, icon: Icon, children, variant }) => (
    <button disabled={busy} onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-2xl px-5 py-2.5 font-semibold text-sm transition-colors disabled:opacity-60 ${VARIANT_CLASSES[variant] || VARIANT_CLASSES.default}`}>
      <Icon className="h-4 w-4" /> {children}
    </button>
  );

  const ACTION_HEADINGS = {
    Manager: "Manager Review",
    HR: "HR Decision",
    Director: "Director Final Decision",
    Employee: "Update Request",
  };

  const s = req.currentStatus;
  const managerCanReview = user.role === "Manager" && s === "Submitted";
  const hrCanAct = user.role === "HR" && ["Submitted", "Manager Reviewed", "Sent Back for More Info"].includes(s);
  const dirCanAct = user.role === "Director" && s === "HR Approved";
  const dirCanComplete = user.role === "Director" && s === "Director Approved";
  const employeeCanResubmit = user.role === "Employee" && req.employeeId === user.id && ["Draft", "Sent Back for More Info"].includes(s);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-medium text-vault-panel/70 hover:text-vault-panel transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="rounded-3xl bg-vault-panel p-7 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-vault-accent">{req.requestType}</div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-vault-cream mt-1">{req.requestTitle}</h1>
          </div>
          <StatusBadge status={req.currentStatus} />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-7">
          <Field label="Employee">{req.employeeName}</Field>
          <Field label="Email">{req.employeeEmail}</Field>
          <Field label="Location">{req.employeeLocation}</Field>
          <Field label="Role">{req.employeeRole}</Field>
          <Field label="Gems Amount"><span className="inline-flex items-center gap-1"><Gem className="h-4 w-4 text-vault-accent" />{req.gemsAmount}</span></Field>
          <Field label="Submitted">{fmt(req.submittedAt)}</Field>
        </div>

        <div className="mt-6">
          <div className="text-xs text-vault-subtle mb-1">Description / Reason</div>
          <p className="text-sm text-vault-cream/90 leading-relaxed">{req.description || "\u2014"}</p>
        </div>
        {req.optionalNotes && (
          <div className="mt-4"><div className="text-xs text-vault-subtle mb-1">Optional Notes</div><p className="text-sm text-vault-cream/90">{req.optionalNotes}</p></div>
        )}

        {(req.managerNotes || req.hrNotes || req.directorNotes) && (
          <div className="grid sm:grid-cols-3 gap-3 mt-6">
            {req.managerNotes && <div className="rounded-2xl bg-vault-panel2 p-4"><div className="text-xs font-bold text-vault-accent mb-1">Manager Notes</div><p className="text-sm text-vault-cream/90">{req.managerNotes}</p></div>}
            {req.hrNotes && <div className="rounded-2xl bg-vault-panel2 p-4"><div className="text-xs font-bold text-vault-accent mb-1">HR Notes \u00b7 {req.hrReviewedBy}</div><p className="text-sm text-vault-cream/90">{req.hrNotes}</p></div>}
            {req.directorNotes && <div className="rounded-2xl bg-vault-panel2 p-4"><div className="text-xs font-bold text-vault-accent mb-1">Director Notes \u00b7 {req.directorReviewedBy}</div><p className="text-sm text-vault-cream/90">{req.directorNotes}</p></div>}
          </div>
        )}
      </div>

      {(managerCanReview || hrCanAct || dirCanAct || dirCanComplete || employeeCanResubmit) && (
        <div className="rounded-3xl bg-vault-panel p-7 sm:p-8">
          <h3 className="font-display text-lg font-bold text-vault-cream mb-3">
            {ACTION_HEADINGS[user.role] || "Update Request"}
          </h3>
          {!employeeCanResubmit && (
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add notes / recommendation (optional)"
              className="bg-vault-panel2 border-white/10 text-vault-cream min-h-[90px] mb-4" />
          )}
          <div className="flex flex-wrap gap-3">
            {managerCanReview && <Btn onClick={() => act("Manager Reviewed", "Manager review added.")} icon={Flag} variant="accent">Mark Reviewed</Btn>}
            {hrCanAct && <>
              <Btn onClick={() => act("HR Approved", "HR approved \u2014 sent to Director.")} icon={CheckCircle2} variant="accent">HR Approve</Btn>
              {s !== "Sent Back for More Info" && <Btn onClick={() => act("Sent Back for More Info", "Sent back for more info.")} icon={Undo2}>Send Back</Btn>}
              <Btn onClick={() => act("HR Denied", "Request denied.")} icon={XCircle} variant="danger">HR Deny</Btn>
            </>}
            {dirCanAct && <>
              <Btn onClick={() => act("Director Approved", "Director approved.")} icon={CheckCircle2} variant="accent">Director Approve</Btn>
              <Btn onClick={() => act("Director Denied", "Request denied.")} icon={XCircle} variant="danger">Director Deny</Btn>
            </>}
            {dirCanComplete && <Btn onClick={() => act("Completed", "Request completed.")} icon={CheckCircle2} variant="accent">Mark Completed</Btn>}
            {employeeCanResubmit && <Btn onClick={() => act("Submitted", "Request submitted.")} icon={Send} variant="accent">Submit Request</Btn>}
          </div>
        </div>
      )}

      <div className="rounded-3xl bg-vault-panel p-7 sm:p-8">
        <h3 className="font-display text-lg font-bold text-vault-cream mb-5">Request Timeline</h3>
        {history.length === 0 ? <p className="text-sm text-vault-subtle">No history yet.</p> : (
          <ol className="relative border-l border-white/10 ml-2 space-y-6">
            {history.slice().reverse().map((h) => (
              <li key={h.id} className="ml-5">
                <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-vault-accent" />
                <div className="flex flex-wrap items-center gap-2">
                  {h.newStatus && <StatusBadge status={h.newStatus} />}
                  <span className="text-xs text-vault-subtle flex items-center gap-1"><Clock className="h-3 w-3" />{fmt(h.createdAt)}</span>
                </div>
                <div className="text-sm text-vault-cream mt-1">{h.action} by <span className="font-semibold">{h.performedByName}</span> ({h.performedByRole})</div>
                {h.notes && <div className="text-xs text-vault-subtle mt-1">\u201c{h.notes}\u201d</div>}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
